import { create } from 'zustand'

// ── Notification Store — toast alerts ────────────────────────────────────────

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message?: string
  durationMs?: number
}

interface NotificationState {
  notifications: Notification[]

  // Actions
  push: (n: Omit<Notification, 'id'>) => void
  dismiss: (id: string) => void
  clear: () => void
}

let idCounter = 0

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  push: (n) => {
    const id = `notif-${++idCounter}`
    const notification: Notification = { id, durationMs: 4000, ...n }

    set((s) => ({ notifications: [...s.notifications, notification] }))

    // Auto-dismiss after durationMs (errors stay until dismissed manually)
    if (n.type !== 'error' && notification.durationMs) {
      setTimeout(() => {
        set((s) => ({ notifications: s.notifications.filter((x) => x.id !== id) }))
      }, notification.durationMs)
    }
  },

  dismiss: (id) => set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),

  clear: () => set({ notifications: [] }),
}))

// ── Convenience helpers ───────────────────────────────────────────────────────

export const notify = {
  success: (title: string, message?: string) =>
    useNotificationStore.getState().push({ type: 'success', title, message }),
  error: (title: string, message?: string) =>
    useNotificationStore.getState().push({ type: 'error', title, message }),
  warning: (title: string, message?: string) =>
    useNotificationStore.getState().push({ type: 'warning', title, message }),
  info: (title: string, message?: string) =>
    useNotificationStore.getState().push({ type: 'info', title, message }),
}
